import { Box, Icon, Popover, SxProps, TableCell, TableRow, TextField, Theme } from "@mui/material";
import { Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableVirtuoso, TableVirtuosoHandle } from "react-virtuoso";

import { ColumnData, Columns, deepClone, deepCopy, equalsObj, includesStringInOrder, invertOrderDirection, OrderSpecifier, resolve, sortArray, SortOpt } from "utils/util";
import { CustomButton } from "../roundedbutton/CustomButton";
import { theme } from "theme";

// se chamar o resolve direto como children do componente, da `Maximum call stack size exceeded`
// mas se chamar dessa forma, sem problemas Â¯\_(ãƒ„)_/Â¯
const resolveWrapper = (data: any, key: string) => resolve(data, key);

export type HeaderExtraParams = {
    sortOpt?: SortOpt,
    setSortOpt: Dispatch<SetStateAction<SortOpt | undefined>>;
    filters: Filters,
    setFilters: Dispatch<SetStateAction<Filters>>,
};

export type InfiniteScrollTableProps = {
    ref: HTMLElement | Window | null,
    tableWidth: number,
    calcWidth: (percent: number | string, padding?: number) => string;
};

type InfiniteScrollVirtuosoProps<T> = {
    data: T[],
    renderItem: (tableProps: InfiniteScrollTableProps & { data: T; index: number; }) => JSX.Element;
    heightScale: number | string;
    minHeight?: string;
    fixedHeaderContent: (tableProps: InfiniteScrollTableProps & HeaderExtraParams) => JSX.Element;
    fixedFooterContent?: (tableProps: InfiniteScrollTableProps & { data: T[]; }) => JSX.Element;
    initialSort?: SortOpt;
    adaptArray?: (data: { root: T[], modified: T[]; }, opt?: { orderBy?: string, orderDirection?: OrderSpecifier; }) => T[];
    onScroll?: (event: any) => void;
    bgcolor?: string;
    emptyRender?: () => JSX.Element;
};


export type HeaderInfiniteScrollVirtuosoProps<T> = InfiniteScrollTableProps & Columns<T> & HeaderExtraParams;

export type RowInfiniteScrollVirtuosoProps<T, C = T> = InfiniteScrollTableProps & Columns<C> & {
    data: T;
    index: number;
    sx?: SxProps<Theme>;
};

type Filters = { [key: string]: { apply: (item: any, row: any) => boolean; value: any; }; };

const InfiniteScrollVirtuoso = forwardRef(<T extends {}>(props: InfiniteScrollVirtuosoProps<T>,
    ref: React.Ref<TableVirtuosoHandle>) => {
    const [tableWidth, _setTableWidth] = useState<number>(0);
    const [_hasScroll, _setHasScroll] = useState(false);

    const [sortOpt, setSortOpt] = useState(props.initialSort);
    const [filters, setFilters] = useState<Filters>({});
    const [height, setHeight] = useState(0);


    useEffect(() => {
        const handleResize = () => {
            setHeight(() => window.innerHeight * Number(props.heightScale));
        };
        if (typeof props.heightScale === "number") {
            handleResize();
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
        //eslint-disable-next-line
    }, [window.innerHeight]);

    const visibleRows = useMemo(
        () => {
            let data = deepCopy(props.data);
            if (!equalsObj(filters, {})) {
                data = data.filter(item => {
                    return Object.entries(filters).every(([key, value]) => {
                        return value.apply(resolveWrapper(item, key), item);
                    });
                });
            }

            data = !sortOpt ? data : sortArray(data, { fieldToSort: sortOpt.orderBy, orderDirection: sortOpt.orderDirection });

            if (props.adaptArray) {
                data = props.adaptArray?.({ root: deepCopy(props.data), modified: data }, { orderBy: sortOpt?.orderBy, orderDirection: sortOpt?.orderDirection });
            }
            return data;
        },
        //eslint-disable-next-line
        [props.adaptArray, sortOpt?.orderDirection, sortOpt?.orderBy, filters, props.data],
    );

    const tableRef = useRef<HTMLElement | Window | null>(null);

    useEffect(() => {
        const node = tableRef.current;
        if (!node) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect) {
                    _setTableWidth(prev => entry.contentRect.width);
                    _setHasScroll(prev => {
                        const div = (node as HTMLDivElement);
                        return div.scrollHeight > div.clientHeight;
                    });
                }
            }
        });

        resizeObserver.observe(node as Element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [tableRef]);

    useEffect(() => {
        const tableWrapper = tableRef.current as HTMLDivElement;
        const div = tableWrapper.querySelector('div');
        const table = div?.querySelector('table');

        setTimeout(() => {
            const tfoot = table?.querySelector('tfoot');
            if (tfoot) {
                // tfoot.style.position = _hasScroll ? 'sticky' : 'absolute';
            }
        }, 70);

    }, [_hasScroll]);


    const calcWidth = useCallback((percent: number | string, padding?: number): string => {
        if (typeof percent === "string") return percent;
        const calculated = (tableWidth * percent) / 100;
        return `${calculated - (padding ?? 0)}px`;
    }, [tableWidth]);

    return (
        <TableVirtuoso
            ref={ref}
            scrollerRef={(ref) => { tableRef.current = ref; }}
            style={{
                height: typeof props.heightScale === "string" ? props.heightScale : height,
                minHeight: props.minHeight ?? '0',
                overflowY: 'auto',
                overflowX: 'auto',
                width: '100%',
                position: 'relative',
                backgroundColor: props.bgcolor
            }}

            components={{
                EmptyPlaceholder: props.emptyRender,
            }}

            className="color-orange"
            fixedHeaderContent={() =>
                props.fixedHeaderContent({
                    ref: tableRef.current,
                    tableWidth,
                    calcWidth,
                    sortOpt,
                    setSortOpt,
                    filters,
                    setFilters,
                })
            }
            fixedFooterContent={props.data.length > 0
                ? () => props.fixedFooterContent?.({ ref: tableRef.current, tableWidth, calcWidth, data: visibleRows })
                : undefined
            }
            data={visibleRows}
            itemContent={(index, row) => props.renderItem({ ref: tableRef.current, tableWidth, calcWidth, data: row, index })}
            onScroll={props.onScroll}
        />
    );
}) as <T extends {}>(props: InfiniteScrollVirtuosoProps<T> & { ref?: React.Ref<TableVirtuosoHandle>; }) => JSX.Element;;

export const HeaderInfiniteScrollVirtuoso = <T extends object>({ calcWidth, columns, sortOpt, setSortOpt, filters, setFilters }: HeaderInfiniteScrollVirtuosoProps<T>) => {
    const handleSort = (property: string) => {
        setSortOpt(prev => {
            const orderDirection = prev?.orderBy !== property ? 'ASC' : invertOrderDirection(prev?.orderDirection);
            return {
                orderBy: property,
                orderDirection,
            };
        });
    };

    useEffect(() => {
        if (!sortOpt) {
            const find = columns.find(c => c.sortable);
            if (find) {
                handleSort(find.key);
            }
        }
        //eslint-disable-next-line
    }, []);

    const getOrderDirection = () => (sortOpt?.orderDirection ?? 'ASC').toLowerCase() as 'asc' | 'desc';

    return (
        <TableRow sx={{
            width: '100%',
        }}>
            {columns.map((c, i) => {
                return <TableCell
                    key={c.id + i + '-header'}
                    sx={{
                        height: '30px',
                        minWidth: c.minWidth,
                        maxWidth: c.maxWidth,
                        width: calcWidth(c.width, 15),
                        bgcolor: theme.palette.background.default,
                        padding: '0 0 0 15px',
                    }}
                    sortDirection={c.sortable && sortOpt?.orderBy === c.key ? getOrderDirection() : false}
                >
                    <HeaderIcons
                        column={c}
                        sortOpt={{
                            columnActive: sortOpt?.orderBy === c.key,
                            direction: getOrderDirection(),
                            onSort: handleSort
                        }}
                        filterOpt={{
                            filters,
                            setFilters,
                        }}
                    />
                </TableCell>;
            })}
        </TableRow >
    );
};

type HeaderIconsProps = {
    column: ColumnData<any>;
    sortOpt?: {
        columnActive: boolean;
        direction: 'asc' | 'desc';
        onSort: (prop: string) => void;
    };
    filterOpt?: {
        filters: Filters,
        setFilters: Dispatch<SetStateAction<Filters>>;
    };
};

const HeaderIcons = ({ column, filterOpt, sortOpt }: HeaderIconsProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenuRow = Boolean(anchorEl);
    const [filterLocal, setFilterLocal] = useState<any>(null);

    const handleAplicarFiltro = () => {
        if (!filterLocal) return handleLimparFiltro();
        filterOpt?.setFilters(prev => {
            return {
                ...prev,
                [column.key]: {
                    value: filterLocal,
                    apply: (item, row) => column.customFilter?.({ filterValue: filterLocal, item, row }) ?? includesStringInOrder(item, filterLocal)
                }
            };
        });
        setAnchorEl(null);
    };

    const handleLimparFiltro = () => {
        filterOpt?.setFilters(prev => {
            const copy = deepClone(prev);
            delete copy[column.key];
            return copy;
        });
        setAnchorEl(null);
        setTimeout(() => {
            setFilterLocal(null);
        }, 100);
    };

    const isActiveColumnSort = sortOpt?.columnActive;
    const sortIcon = !isActiveColumnSort
        ? 'fa-solid fa-right-left'
        : sortOpt?.direction === 'asc'
            ? "fa-solid fa-arrow-up-short-wide"
            : "fa-solid fa-arrow-down-wide-short";

    return <Box
        sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            justifyContent: column.textAlign,
            fontWeight: '600',
            fontSize: '14px',
            color: theme.palette.common.black
        }}
    >
        {column.header}

        {column.sortable &&
            <Icon className={sortIcon} onClick={() => sortOpt?.onSort(column.key)} sx={{ cursor: 'pointer', transform: !isActiveColumnSort ? 'rotate(90deg)' : undefined, fontSize: '15px', color: isActiveColumnSort ? theme.palette.primary.main : theme.palette.text.primary }} />
        }

        {column.filterable && <>
            <Icon onClick={(e) => setAnchorEl(e.currentTarget)} className={'fa-solid fa-filter'} sx={{ cursor: 'pointer', fontSize: '15px', color: filterLocal ? theme.palette.primary.main : theme.palette.text.primary, }} />

            <Popover
                open={openMenuRow}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                sx={{
                    marginTop: '5px',
                }}
            >
                <Box sx={{ padding: '15px' }}>
                    {column.customFilterJSX?.({ column, filter: filterLocal, setFilter: setFilterLocal }) ??
                        <TextField
                            label={'Filtrar por ' + column.header}
                            variant="standard"
                            value={filterLocal ?? ''}
                            onChange={(e) => setFilterLocal(e.target.value)}
                        />
                    }
                    <Box display={'flex'} justifyContent={'space-between'} marginTop={'15px'}>
                        <CustomButton
                            disabled={false}
                            onClick={handleLimparFiltro}
                            variant="contained"
                            color='primary'
                        >
                            Limpar
                        </CustomButton>

                        <CustomButton
                            disabled={false}
                            onClick={handleAplicarFiltro}
                            variant="contained"
                            color="success"
                        >
                            Aplicar
                        </CustomButton>
                    </Box>
                </Box>
            </Popover>
        </>
        }
    </Box>;
};

export const RowInfiniteScrollVirtuoso = <T extends Object, C = T>({ calcWidth, columns, data, index, sx }: RowInfiniteScrollVirtuosoProps<T, C>) => {

    return (
        <>
            {columns.map((c, i) => {
                return <TableCell
                    key={c.key + i + index + '-row'}
                    sx={{
                        height: '30px',
                        minWidth: c.minWidth,
                        maxWidth: c.maxWidth,
                        width: calcWidth(c.width),
                        textAlign: c.textAlign,
                        fontSize: '14px',
                        padding: 0,
                        paddingLeft: 0,
                        color: theme.palette.common.black,
                        ...sx
                    }}
                >
                    {c.convert?.(data as any, { column: c }) ?? resolveWrapper(data, c.key)}
                </TableCell>;
            })}
        </>
    );
};

export default InfiniteScrollVirtuoso;
